import { cliDownloadUrl } from '@mogzo/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { CopyCommand } from '../components/CopyCommand';
import { Page } from '../components/Page';
import { ProjectStatusChip } from '../components/ProjectStatus';
import { Alert, Button, buttonClass, cardClass, ErrorNotice, Loading } from '../components/ui';
import { ApiError, apiSend, type Project } from '../lib/api';
import { useDeveloper } from '../lib/developer';
import { formatDate, initials, tileClass } from '../lib/format';
import { useApi } from '../lib/use-api';

interface ProjectList {
  projects: Project[];
  archivedCount: number;
}

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function Code({ children }: { children: string }) {
  return (
    <code className="rounded-md bg-field px-1.5 py-0.5 font-mono text-[13px] text-ink">
      {children}
    </code>
  );
}

/** First visit: no projects yet (PRD 6.1, step 5). */
function Welcome() {
  const me = useDeveloper();
  const firstName = me.displayName.split(/\s+/)[0] ?? me.displayName;
  return (
    <>
      <section className="relative overflow-hidden rounded-[12px] bg-inverse px-8 py-9 text-on-inverse sm:px-10">
        <p className="text-xs font-semibold tracking-[0.25em] text-brand uppercase">
          Welcome to MOGZO developers
        </p>
        <h1 className="mt-3 max-w-2xl text-2xl font-semibold sm:text-[34px]">
          Hi {firstName}. Let’s get your first game on MOGZO.
        </h1>
        <p className="mt-2.5 max-w-xl leading-relaxed text-on-inverse/80">
          Two-factor is on, so your developer account is ready. Start with a project: it holds one
          game’s builds, testers and settings.
        </p>
        <img
          src="/brand/mascot.svg"
          alt=""
          className="pointer-events-none absolute right-8 -bottom-5 hidden h-36 w-auto sm:block"
        />
      </section>

      <section aria-labelledby="steps-heading" className={`${cardClass} px-6 py-6 sm:px-8`}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="steps-heading" className="text-xl font-semibold">
            Make your first game
          </h2>
          <Link
            to="/docs"
            className="font-semibold underline decoration-brand decoration-2 underline-offset-4"
          >
            Read the docs
          </Link>
        </div>
        <ol className="mt-4">
          <li className="space-y-3 border-t border-divider py-4">
            <span className="flex items-center gap-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand font-semibold">
                1
              </span>
              <span>
                <strong className="block">Install the MOGZO CLI</strong>
                <span className="text-sm text-muted">It needs Node.js 22 or newer.</span>
              </span>
            </span>
            <CopyCommand command={`npm install -g ${cliDownloadUrl(window.location.origin)}`} />
          </li>
          <li className="space-y-3 border-t border-divider py-4">
            <span className="flex items-center gap-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line bg-field font-semibold">
                2
              </span>
              <span>
                <strong className="block">Sign in, and make a game</strong>
                <span className="text-sm text-muted">
                  You approve the sign-in here. <Code>mogzo create</Code> makes the project and a
                  folder with a small working game.
                </span>
              </span>
            </span>
            <CopyCommand command="mogzo login" />
            <CopyCommand command={'mogzo create "My Game"'} />
          </li>
          <li className="flex flex-wrap items-center gap-4 border-t border-divider py-4">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line bg-field font-semibold">
              3
            </span>
            <span className="min-w-0 flex-[1_1_320px]">
              <strong className="block">Play it locally, then upload</strong>
              <span className="text-sm text-muted">
                In the game’s folder: <Code>mogzo dev</Code> to play it as a test player,{' '}
                <Code>mogzo upload</Code> to post it to your private test community.
              </span>
            </span>
          </li>
        </ol>
        <p className="mt-2 text-sm text-muted">
          Prefer to set up the project first?{' '}
          <Link to="/projects/new" className="font-semibold text-ink underline">
            Create it here
          </Link>{' '}
          and use its address in <Code>mogzo create --address</Code>.
        </p>
      </section>
    </>
  );
}

function ArchivedProjects({ onRestored }: { onRestored: () => void }) {
  const [version, setVersion] = useState(0);
  const result = useApi<ProjectList>('/v1/dev/projects?archived=true', version);
  const [error, setError] = useState<string | null>(null);

  async function restore(project: Project) {
    setError(null);
    try {
      await apiSend('POST', `/v1/dev/projects/${project.id}/restore`);
      setVersion((n) => n + 1);
      onRestored();
    } catch (failed) {
      setError(failed instanceof ApiError ? failed.message : 'Could not reach the API. Try again.');
    }
  }

  if (result.status === 'loading') return <Loading />;
  if (result.status === 'error') return <ErrorNotice error={result.error} />;
  return (
    <>
      {error && <Alert tone="error">{error}</Alert>}
      {result.data.projects.map((project) => (
        <div
          key={project.id}
          className={`${cardClass} flex flex-wrap items-center justify-between gap-3 px-6 py-4`}
        >
          <span>
            <span className="block font-semibold">{project.name}</span>
            <span className="block font-mono text-[13px] text-muted">
              {project.slug} · archived {formatDate(project.archivedAt ?? project.createdAt)}
            </span>
          </span>
          <Button variant="secondary" onClick={() => restore(project)}>
            Restore
          </Button>
        </div>
      ))}
    </>
  );
}

export function Projects() {
  const [version, setVersion] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const result = useApi<ProjectList>('/v1/dev/projects', version);

  const description = 'One project per game: its builds, test community, data and store listing.';
  if (result.status !== 'ready') {
    return (
      <Page title="Projects" description={description}>
        {result.status === 'loading' ? <Loading /> : <ErrorNotice error={result.error} />}
      </Page>
    );
  }

  const { projects, archivedCount } = result.data;
  if (projects.length === 0 && archivedCount === 0) {
    return (
      <Page title="Projects" description={description}>
        <Welcome />
      </Page>
    );
  }

  return (
    <Page
      title="Projects"
      description={description}
      actions={
        <Link to="/projects/new" className={buttonClass('primary')}>
          <PlusIcon />
          New project
        </Link>
      }
    >
      {projects.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
          {projects.map((project, index) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className={`${cardClass} flex flex-col gap-3.5 px-6 py-5 hover:border-muted`}
            >
              <span className="flex items-start justify-between gap-3">
                <span className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={`flex size-11 shrink-0 items-center justify-center rounded-[6px] font-semibold ${tileClass(index)}`}
                  >
                    {initials(project.name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[15px] font-semibold">{project.name}</span>
                    <span className="block font-mono text-[13px] text-muted">{project.slug}</span>
                  </span>
                </span>
                <ProjectStatusChip project={project} />
              </span>
              <span className="flex justify-between gap-3 border-t border-divider pt-3.5 text-[13px] text-muted">
                <span>
                  {project.liveVersion
                    ? `Live: ${project.liveVersion}${project.reviewVersion ? ` · ${project.reviewVersion} in review` : ''}`
                    : project.reviewVersion
                      ? `${project.reviewVersion} in review`
                      : `Created ${formatDate(project.createdAt)}`}
                </span>
                <span className="font-semibold text-ink">Open</span>
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className={`${cardClass} px-6 py-8 text-center text-muted`}>
          No active projects. Restore an archived one or create a new project.
        </p>
      )}

      {archivedCount > 0 && (
        <div className={`${cardClass} flex flex-wrap items-center justify-between gap-3 px-5 py-4`}>
          <p className="text-sm text-muted">
            <strong className="text-ink">
              {archivedCount} archived project{archivedCount === 1 ? '' : 's'}.
            </strong>{' '}
            Archived projects are hidden here, and nothing in them is deleted.
          </p>
          <Button variant="secondary" onClick={() => setShowArchived((open) => !open)}>
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        </div>
      )}
      {showArchived && <ArchivedProjects onRestored={() => setVersion((n) => n + 1)} />}
    </Page>
  );
}
